// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiParameter, get, post } from "@insite/client-framework/Services/ApiService";
import { ProductCollectionModel, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import { CustomLoadInventoryApiParameter } from "@plumberssupply/App/Handlers/SystemBuilder/loadCustomRealTimeInventory";
import { System } from "@plumberssupply/App/Store/Reducers";

const systemBuilderApi = "/api/pls/system-builder";
const productsUrl = "/api/v2/products";

export interface GetUnitaryDropDownResult {
    values: string[];
    successful: boolean;
}

export interface BuildMiniSplitSystemsResult {
    systems: System[];
    successful: boolean;
}

export interface BuildUnitarySystemsResult {
    systems: System[];
    successful: boolean;
}

export async function buildMiniSplitSystem(parameter: any) {
    try {
        const result = await post<BuildMiniSplitSystemsResult>(
            `${systemBuilderApi}/build-mini-split-systems`,
            parameter,
        );
        return {
            successful: true,
            systems: result.systems,
        };
    } catch (error) {
        return {
            successful: false,
            systems: [],
        };
    }
}

export async function buildUnitarySystem(parameter: any) {
    try {
        const result = await post<BuildUnitarySystemsResult>(`${systemBuilderApi}/build-unitary-systems`, parameter);
        return {
            successful: true,
            systems: result.systems,
        };
    } catch (error) {
        return {
            successful: false,
            systems: [],
        };
    }
}
export interface GetUnitaryTonsParameter extends ApiParameter {
    brand: string;
}

export interface GetUnitaryIndoorParameter extends GetUnitaryTonsParameter {
    tons: number;
}
export interface GetUnitaryFurnaceBtusParameter extends GetUnitaryIndoorParameter {
    indoorUnitType: string;
}

export interface GetMiniSplitNumberOfZonesParameter extends ApiParameter {
    brand: string;
}

export interface GetMiniSplitBtusParameter extends GetMiniSplitNumberOfZonesParameter {
    btusType: string;
    numberOfZones: string;
}
export interface GetMiniSplitUnitTypes extends GetMiniSplitNumberOfZonesParameter {
    btusType: string;
    btus: string;
}

export async function getUnitaryIndoorTypes(parameters: GetUnitaryIndoorParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unitary-indoor-types`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getUnitaryFurnaceBtus(parameters: GetUnitaryFurnaceBtusParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unitary-furnace-btus`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getUnitaryCoilWidths(parameters: GetUnitaryIndoorParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unitary-coil-width-options`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getUnitaryKilowattRatings(parameters: GetUnitaryIndoorParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unitary-kilowatt-ratings`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export interface GetRelatedProductsParameter extends ApiParameter {
    relationship: string;
    withPagination: boolean;
    page: number;
    productId: string;
}

export function getSystemRelatedProducts(parameter: GetRelatedProductsParameter) {
    const { productId, ...newParameter } = parameter;
    return get<ProductCollectionModel>(`${productsUrl}/${productId}/relatedproducts`, newParameter);
}

export async function getUnitaryTons(parameters: GetUnitaryTonsParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unitary-tons`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getUnitaryBrands() {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unitary-brands`);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getMiniSplitBrands() {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/brands`);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}
export async function getMiniSplitZones(parameters: GetMiniSplitNumberOfZonesParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/number-of-zones`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getMiniSplitBtus(parameters: GetMiniSplitBtusParameter) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/btus`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export async function getZoneUnitTypes(parameters: GetMiniSplitUnitTypes) {
    try {
        const result = await get<{ values: string[] }>(`${systemBuilderApi}/unit-types`, parameters);
        return {
            successful: true,
            values: result.values,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            values: [],
        };
    }
}

export function getProductsCustomRealTimeInventory(parameter: CustomLoadInventoryApiParameter) {
    return post<CustomLoadInventoryApiParameter, RealTimeInventoryModel>(`${systemBuilderApi}/real-time-inventory`, {
        productIds: parameter.productIds,
        qtys: parameter.qtys,
    });
}
